/**
 * navigation.js - Navigation API abstraction with History API fallback
 *
 * Handles:
 * - Feature detection for Navigation API
 * - Unified history updates with state
 * - Back/forward navigation with content restoration
 * - Fallback to History API for older browsers
 */

import { swap, extractFragment } from './request.js';
import { hasAttr } from './cascade.js';

/**
 * Feature detection for Navigation API
 * @type {boolean}
 */
export const hasNavigationAPI = typeof window !== 'undefined'
  && 'navigation' in window;

/**
 * Reference to the onInit callback for reinitializing content
 * @type {Function|null}
 */
let initCallback = null;

/**
 * Update browser history with state
 *
 * For forward navigation (push/replace after content swap), we use History API
 * directly since the content swap has already happened. Navigation API's navigate()
 * would trigger a new navigation event which we don't want.
 *
 * Navigation API is only used for intercepting back/forward (traverse) navigation.
 *
 * @param {HTMLElement} el - Triggering element
 * @param {string} url - URL to navigate to
 * @param {Object} state - State to store with navigation
 * @param {string} state.target - Target selector
 * @param {string} state.swap - Swap strategy
 */
export function updateHistory(el, url, state) {
  const historyState = {
    ...state,
    scrollY: window.scrollY,
    timestamp: Date.now(),
    htmlStar: true  // Mark as html-star navigation for traverse detection
  };

  // Always use History API for forward navigation (push/replace)
  // Content swap has already happened, we just need to update URL and state
  if (hasAttr(el, 'push')) {
    // Save current page's state before pushing (for back navigation to work)
    const currentState = {
      target: state.target,
      swap: state.swap,
      select: state.select,
      scrollY: window.scrollY,
      timestamp: Date.now(),
      htmlStar: true
    };

    // Update both History API and Navigation API state for current entry
    if (!history.state?.htmlStar) {
      history.replaceState(currentState, '');
    }
    if (hasNavigationAPI) {
      navigation.updateCurrentEntry({ state: currentState });
    }

    // Push new entry
    history.pushState(historyState, '', url);

    // Update Navigation API state for the new entry
    if (hasNavigationAPI) {
      navigation.updateCurrentEntry({ state: historyState });
    }
  } else if (hasAttr(el, 'replace')) {
    history.replaceState(historyState, '', url);
    if (hasNavigationAPI) {
      navigation.updateCurrentEntry({ state: historyState });
    }
  }
}

/**
 * Handle back/forward navigation (traverse)
 *
 * @param {string} url - Destination URL
 * @param {Object} state - Stored state
 * @returns {Promise}
 */
async function handleTraverse(url, state) {
  if (!state?.htmlStar || !state?.target) {
    // Not an html-star navigation or no target, fall back to reload
    location.reload();
    return;
  }

  try {
    const response = await fetch(url, {
      headers: { 'X-Requested-With': 'htmlstar' }
    });

    if (!response.ok) {
      throw new Error(response.statusText);
    }

    let html = await response.text();
    const target = document.querySelector(state.target);

    // Extract fragment if select was specified
    if (state.select) {
      html = extractFragment(html, state.select);
    }

    if (target) {
      await swap(target, html, state.swap || 'inner');

      // Restore scroll position AFTER transition completes
      // Use requestAnimationFrame to ensure DOM has settled
      if (typeof state.scrollY === 'number') {
        requestAnimationFrame(() => {
          window.scrollTo(0, state.scrollY);
        });
      }

      // Reinitialize new content
      if (initCallback) {
        initCallback(target);
      }

      // Dispatch navigation event for UI sync (e.g., updating active nav state)
      window.dispatchEvent(new CustomEvent('htmlstar:navigated', {
        detail: {
          url: url,
          target: state.target,
          swap: state.swap,
          isTraverse: true
        }
      }));
    }
  } catch (err) {
    console.error('htmlstar: navigation error', err);
    // Fall back to reload on error
    location.reload();
  }
}

/**
 * Initialize modern Navigation API handler
 */
function initModern() {
  navigation.addEventListener('navigate', (event) => {
    // Only handle traverse (back/forward)
    if (event.navigationType !== 'traverse') return;
    if (!event.canIntercept) return;

    const state = event.destination.getState();
    if (!state?.htmlStar || !state?.target) {
      // Not an html-star navigation, reload to get correct content
      location.reload();
      return;
    }

    event.intercept({
      scroll: 'after-transition',
      focusReset: 'after-transition',
      async handler() {
        await handleTraverse(event.destination.url, state);
      }
    });
  });
}

/**
 * Initialize legacy History API handler
 */
function initLegacy() {
  window.addEventListener('popstate', async (event) => {
    await handleTraverse(location.href, event.state);
  });
}

/**
 * Initialize navigation handling
 * Sets up either Navigation API or History API popstate listener
 *
 * @param {Function} onInit - Callback to reinitialize new content
 */
export function initNavigation(onInit) {
  initCallback = onInit;

  if (hasNavigationAPI) {
    initModern();
  } else {
    initLegacy();
  }
}
